<template>
    <div id="report"
         class="report-container print-content"
         :class="{'report--extended-print': extended}">
        <div id="report-info">
            <h2 v-if="printTitle"
                class="print-only">
                {{ l(printTitle) }}
            </h2>

            <!-- Trading Session Info -->
            <div class="row">
                <div class="col-md-3 text-center">
                    <h4>{{ l('REPORTS.DEVICE') }}</h4>
                    <div>
                        {{ tradingSession.device.name }}
                        <div v-if="tradingSession.deviceID && tradingSession.deviceID !== currentDevice.ID"
                             class="no-print">
                            <span class="label label-warning">{{ l('REPORTS.ANOTHER_DEVICE') }}</span>
                        </div>
                    </div>
                </div>

                <div class="col-md-3 text-center">
                    <h4>{{ l('REPORTS.TRADING_SESSION_ID') }}</h4>
                    <div>{{ tradingSession.ID }}</div>
                </div>

                <div class="col-md-3 text-center">
                    <h4>{{ l('REPORTS.DATE_STARTED') }}</h4>
                    <div>{{ dateFilter(tradingSession.startedAt, true) }}</div>
                </div>

                <div class="col-md-3 text-center">
                    <h4>{{ l('REPORTS.DATE_ENDED') }}</h4>
                    <div v-if="tradingSession.endedAt">
                        {{ dateFilter(tradingSession.endedAt, true) }}
                    </div>
                    <div v-else>
                        <em>{{ l('REPORTS.NOT_ENDED') }}</em>
                    </div>
                </div>

                <div class="col-md-4 text-center print-only">
                    <h4>{{ l('REPORTS.PRINTED_AT') }}</h4>
                    <div>{{ dateFilter(new Date(), true) }}</div>
                </div>

                <div class="col-md-4 text-center print-only">
                    <h4>{{ l('REPORTS.PRINTED_BY') }}</h4>
                    <div>{{ currentUsername }}</div>
                </div>

                <div v-if="tradingSession.deviceID !== currentDevice.ID"
                     class="col-md-4 text-center print-only">
                    <h4>{{ l('REPORTS.PRINTED_ON_DEVICE') }}</h4>
                    <div>{{ currentDevice.name }}</div>
                </div>
            </div>

            <div class="report-grid">
                <!-- Payment Type Analysis -->
                <div class="report-data__cell">
                    <table class="report table">
                        <thead>
                            <tr>
                                <th colspan="3">
                                    {{ l('REPORTS.PAYMENTS_HEADING') }}
                                </th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.PAYMENTS_METHOD_HEADING') }}</th>
                                <th>{{ l('REPORTS.PAYMENTS_COUNT_HEADING') }}</th>
                                <th>{{ l('REPORTS.PAYMENTS_VALUE_HEADING') }}</th>
                            </tr>
                        </thead>
                        <tbody v-for="paymentMethod in summary.payments"
                               :key="paymentMethod.ID">
                            <tr v-if="paymentMethod.salesTotalCount > 0">
                                <td>{{ paymentMethod.paymentMethodName }}</td>
                                <td>{{ suppressZeroFilter(paymentMethod.salesTotalCount, numberFilter) }}</td>
                                <td>{{ suppressZeroFilter(paymentMethod.salesTotalValue, currencyFilter) }}</td>
                            </tr>
                            <tr v-if="paymentMethod.refundsTotalCount > 0">
                                <td>
                                    {{ paymentMethod.paymentMethodName }}
                                    <small>{{ l('REPORTS.PAYMENTS_REFUNDS_LABEL') }}</small>
                                </td>
                                <td>{{ suppressZeroFilter(paymentMethod.refundsTotalCount, numberFilter) }}</td>
                                <td>{{ suppressZeroFilter(-paymentMethod.refundsTotalValue, currencyFilter) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th>{{ l('REPORTS.PAYMENTS_TOTAL_PAYMENTS_HEADING') }}</th>
                                <th>{{ numberFilter(summary.paymentsSummary.sales.totalCount) }}</th>
                                <th>{{ currencyFilter(summary.paymentsSummary.sales.totalValue) }}</th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.PAYMENTS_TOTAL_DEPOSITS_HEADING') }}</th>
                                <th>{{ numberFilter(summary.depositTotals.paymentCount) }}</th>
                                <th>{{ currencyFilter(summary.depositTotals.totalAmountReceived) }}</th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.PAYMENTS_TOTAL_REFUNDS_HEADING') }}</th>
                                <th>{{ numberFilter(summary.paymentsSummary.refunds.totalCount) }}</th>
                                <th>{{ currencyFilter(-summary.paymentsSummary.refunds.totalValue) }}</th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.PAYMENTS_TOTAL_HEADING') }}</th>
                                <th>{{ numberFilter(summary.paymentsSummary.net.totalCount) }}</th>
                                <th>{{ currencyFilter(summary.paymentsSummary.net.totalValue) }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- Till Contents -->
                <div class="report-data__cell">
                    <table class="report table">
                        <thead>
                            <tr>
                                <th colspan="3">
                                    {{ l('REPORTS.TILL_CONTENTS_HEADING') }}
                                </th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.TILL_CONTENTS_TYPE_HEADING') }}</th>
                                <th>{{ l('REPORTS.TILL_CONTENTS_COUNT_HEADING') }}</th>
                                <th>{{ l('REPORTS.TILL_CONTENTS_VALUE_HEADING') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ l('REPORTS.TILL_CONTENTS_FLOAT_HEADING') }}</td>
                                <td></td>
                                <td>{{ suppressZeroFilter(summary.tillContents.initialFloat, currencyFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_CONTENTS_CASH_IN_HEADING') }}</td>
                                <td></td>
                                <td>{{ suppressZeroFilter(summary.tillContents.cashTendered, currencyFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_CONTENTS_CHANGE_HEADING') }}</td>
                                <td></td>
                                <td>{{ suppressZeroFilter(-summary.tillContents.changeExcludingRefunds, currencyFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_CONTENTS_REFUNDS_HEADING') }}</td>
                                <td></td>
                                <td>{{ suppressZeroFilter(-summary.tillContents.refundsGiven, currencyFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_CONTENTS_CASHBACK_HEADING') }}</td>
                                <td></td>
                                <td>{{ suppressZeroFilter(-summary.tillContents.cashbackGiven, currencyFilter) }}</td>
                            </tr>
                            <tr>
                                <td v-if="summary.floatAdjustmentsTotalCount > 0"
                                    colspan="3"
                                    class="text-center">
                                    <em>{{ l('REPORTS.TILL_ACTIVITY_FLOAT_ADJUSTMENTS') }}</em>
                                </td>
                            </tr>
                            <tr v-for="(adjustment, reason) in summary.tillContentsAdjustments"
                                :key="reason">
                                <td>{{ reason }}</td>
                                <td>{{ adjustment.totalCount }}</td>
                                <td>{{ suppressZeroFilter(adjustment.totalValue, currencyFilter) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th>{{ l('REPORTS.TILL_CONTENTS_TOTAL_HEADING') }}</th>
                                <th></th>
                                <th>{{ currencyFilter(summary.tillContentsTotalValue) }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- Till Activity -->
                <div class="report-data__cell">
                    <table class="report table">
                        <thead>
                            <tr>
                                <th colspan="2">
                                    {{ l('REPORTS.TILL_ACTIVITY_HEADING') }}
                                </th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.TILL_ACTIVITY_TYPE_HEADING') }}</th>
                                <th>{{ l('REPORTS.TILL_ACTIVITY_COUNT_HEADING') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ l('REPORTS.TILL_ACTIVITY_NOSALES') }}</td>
                                <td>{{ suppressZeroFilter(summary.noSalesTotalCount, numberFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_ACTIVITY_FLOAT_ADJUSTMENTS') }}</td>
                                <td>{{ suppressZeroFilter(summary.floatAdjustmentsTotalCount, numberFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_ACTIVITY_ITEMS_SOLD_HEADING') }}</td>
                                <td>{{ suppressZeroFilter(summary.itemSalesSummary.sales.totalCount, numberFilter) }}</td>
                            </tr>
                            <tr>
                                <td>{{ l('REPORTS.TILL_ACTIVITY_ITEMS_RETURNED_HEADING') }}</td>
                                <td>{{ suppressZeroFilter(summary.itemSalesSummary.returns.totalCount, numberFilter) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Cell containing shorter sub cells -->
                <div>
                    <!-- This extra div means that the child cells don't extend to full height -->
                    <!-- Deposits -->
                    <div class="report-data__cell">
                        <table class="report table">
                            <thead>
                                <tr>
                                    <th colspan="3">
                                        {{ l('REPORTS.DEPOSITS') }}
                                    </th>
                                </tr>
                                <tr>
                                    <th>{{ l('REPORTS.DEPOSITS_TYPE_HEADING') }}</th>
                                    <th>{{ l('REPORTS.DEPOSITS_COUNT_HEADING') }}</th>
                                    <th>{{ l('REPORTS.DEPOSITS_VALUE_HEADING') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{{ l('REPORTS.DEPOSITS_ORDERS_HEADING') }}</td>
                                    <td>{{ summary.depositTotals.orderCount }}</td>
                                    <td>{{ currencyFilter(summary.depositTotals.totalOrderAmount) }}</td>
                                </tr>
                                <tr>
                                    <td>{{ l('REPORTS.DEPOSITS_PAYMENTS_HEADING') }}</td>
                                    <td>{{ summary.depositTotals.paymentCount }}</td>
                                    <td>{{ currencyFilter(summary.depositTotals.totalAmountReceived) }}</td>
                                </tr>
                                <tr>
                                    <td>{{ l('REPORTS.DEPOSITS_OUTSTANDING_HEADING') }}</td>
                                    <td>.</td>
                                    <td>{{ currencyFilter(summary.depositTotals.totalOutstandingBalance) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Vouchers Taken -->
                    <div class="v-space report-data__cell">
                        <table class="report table">
                            <thead>
                                <tr>
                                    <th colspan="3">
                                        {{ l('REPORTS.VOUCHERS_HEADING') }}
                                    </th>
                                </tr>
                                <tr>
                                    <th>{{ l('REPORTS.VOUCHERS_DENOMINATION_HEADING') }}</th>
                                    <th>{{ l('REPORTS.VOUCHERS_COUNT_HEADING') }}</th>
                                    <th>{{ l('REPORTS.VOUCHERS_VALUE_HEADING') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(voucher, value) in summary.vouchers"
                                    :key="value">
                                    <td>{{ currencyFilter(value) }}</td>
                                    <td>{{ voucher.totalCount }}</td>
                                    <td>{{ currencyFilter(value * voucher.totalCount) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>{{ l('REPORTS.VOUCHERS_TOTAL_HEADING') }}</th>
                                    <th>{{ summary.vouchersTotalCount }}</th>
                                    <th>{{ currencyFilter(summary.vouchersTotalValue) }}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <!-- Flexi Vouchers -->
                    <div class="v-space report-data__cell">
                        <table class="report table">
                            <thead>
                                <tr>
                                    <th colspan="3">
                                        {{ l('REPORTS.FLEXI_VOUCHER_HEADING') }}
                                    </th>
                                </tr>
                                <tr>
                                    <th>{{ l('REPORTS.FLEXI_VOUCHER_TYPE_HEADING') }}</th>
                                    <th>{{ l('REPORTS.VOUCHERS_COUNT_HEADING') }}</th>
                                    <th>{{ l('REPORTS.VOUCHERS_VALUE_HEADING') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{{ l('REPORTS.FLEXI_VOUCHER_TENDERED_HEADING') }}</td>
                                    <td>{{ summary.flexiVoucherSummary.salesTotalCount }}</td>
                                    <td>{{ currencyFilter(summary.flexiVoucherSummary.tenderedTotalValue) }}</td>
                                </tr>
                                <tr>
                                    <td>{{ l('REPORTS.FLEXI_VOUCHER_CHANGE_HEADING') }}</td>
                                    <td>.</td>
                                    <td>{{ currencyFilter(-summary.flexiVoucherSummary.changeExcludingRefunds) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>{{ l('REPORTS.FLEXI_VOUCHER_TOTAL_HEADING') }}</th>
                                    <th></th>
                                    <th>{{ currencyFilter(summary.flexiVoucherSummary.salesTotalValue) }}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <!-- tradingSessionStatusID 3 = 'Counted', tradingSessionStatusID 4 = 'Banked' -->
                    <div v-if="[3, 4].includes(tradingSession.tradingSessionStatusID)"
                         class="v-space report-data__cell">
                        <table class="report table">
                            <thead>
                                <tr>
                                    <th colspan="4">
                                        {{ l('REPORTS.CASH_UP_HEADING') }}
                                    </th>
                                </tr>
                                <tr>
                                    <th>{{ l('REPORTS.CASH_UP_DENOMINATION') }}</th>
                                    <th>{{ l('REPORTS.CASH_UP_COUNT') }}</th>
                                    <th>{{ l('REPORTS.CASH_UP_VALUE') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in summary.tillContents.cashActualBreakdown"
                                    :key="row.denomination">
                                    <td>{{ row.denomination }}</td>
                                    <td>{{ numberFilter(row.qty) }}</td>
                                    <td>{{ currencyFilter(row.value) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>
                                        {{ l('REPORTS.CASH_UP_TOTAL_HEADING') }}
                                    </th>
                                    <th></th>
                                    <th>{{ currencyFilter(cashUpTotal) }}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <!-- Redemptions (Extended) -->
                <div class="report-data__cell report__extended-info">
                    <table class="report table">
                        <thead>
                            <tr>
                                <th colspan="5">
                                    {{ l('REPORTS.REDEMPTIONS_HEADING') }}
                                </th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.REDEMPTIONS_TICKET_HEADING') }}</th>
                                <th>{{ l('REPORTS.REDEMPTIONS_PRICE_HEADING') }}</th>
                                <th>{{ l('REPORTS.REDEMPTIONS_COUNT_HEADING') }}</th>
                                <th>
                                    <span class="screen-only">{{ l('REPORTS.REDEMPTIONS_PEOPLE_HEADING') }}</span>
                                    <span class="print-only">{{ l('REPORTS.REDEMPTIONS_PEOPLE_HEADING_PRINT') }}</span>
                                </th>
                                <th>{{ l('REPORTS.REDEMPTIONS_VALUE_HEADING') }}</th>
                            </tr>
                        </thead>
                        <tbody v-for="item in summary.redemptions"
                               :key="item.ID">
                            <tr v-if="item.redemptionsTotalCount > 0">
                                <td class="ticket-title">
                                    {{ item.itemName }}
                                </td>
                                <td class="no-wrap">
                                    <template v-if="item.itemPrice !== null">
                                        {{ currencyFilter(item.itemPrice) }}
                                    </template>
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(item.redemptionsTotalCount) }}
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(item.redemptionsTotalPeople) }}
                                </td>
                                <td class="no-wrap">
                                    <template v-if="item.redemptionsTotalValue !== null">
                                        {{ currencyFilter(item.redemptionsTotalValue) }}
                                    </template>
                                </td>
                            </tr>
                            <tr v-if="item.unredemptionsTotalCount > 0">
                                <td class="ticket-title">
                                    {{ item.itemName }}
                                    <small>{{ l('REPORTS.REDEMPTIONS_UNREDEMPTIONS_LABEL') }}</small>
                                </td>
                                <td class="no-wrap">
                                    <template v-if="item.itemPrice !== null">
                                        {{ currencyFilter(item.itemPrice) }}
                                    </template>
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(-item.unredemptionsTotalCount) }}
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(-item.unredemptionsTotalPeople) }}
                                </td>
                                <td class="no-wrap">
                                    <template v-if="item.unredemptionsTotalValue !== null">
                                        {{ currencyFilter(-item.unredemptionsTotalValue) }}
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th>{{ l('REPORTS.REDEMPTIONS_TOTALS_HEADING') }}</th>
                                <th></th>
                                <th>{{ numberFilter(summary.redemptionsSummary.net.totalCount) }}</th>
                                <th>{{ numberFilter(summary.redemptionsSummary.net.totalPeople) }}</th>
                                <th>{{ currencyFilter(summary.redemptionsSummary.net.totalValue) }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- Item Sales (Extended) -->
                <div class="report-data__cell report__extended-info">
                    <table class="report table">
                        <thead>
                            <tr>
                                <th colspan="4">
                                    {{ l('REPORTS.ITEM_SALES_HEADING') }}
                                </th>
                            </tr>
                            <tr>
                                <th>{{ l('REPORTS.ITEM_SALES_CATEGORY_OR_ITEM_HEADING') }}</th>
                                <th>{{ l('REPORTS.ITEM_SALES_PRICE_HEADING') }}</th>
                                <th>{{ l('REPORTS.ITEM_SALES_COUNT_HEADING') }}</th>
                                <th>{{ l('REPORTS.ITEM_SALES_VALUE_HEADING') }}</th>
                            </tr>
                        </thead>
                        <tbody v-for="(item, index) in summary.itemSales"
                               :key="item.ID">
                            <tr v-if="index === 0 || summary.itemSales[index - 1].categoryID !== item.categoryID">
                                <td colspan="4">
                                    <em>{{ item.categoryName }}</em>
                                </td>
                            </tr>
                            <tr v-if="item.soldTotalCount > 0">
                                <td class="item-sales-item">
                                    {{ item.itemName }}
                                </td>
                                <td class="no-wrap">
                                    {{ currencyFilter(item.itemPrice) }}
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(item.soldTotalCount) }}
                                </td>
                                <td class="no-wrap">
                                    {{ currencyFilter(item.soldTotalValue) }}
                                </td>
                            </tr>
                            <tr v-if="item.returnedTotalCount > 0">
                                <td class="item-sales-item">
                                    {{ item.itemName }}
                                    <small>{{ l('REPORTS.ITEM_SALES_RETURNS_LABEL') }}</small>
                                </td>
                                <td class="no-wrap">
                                    {{ currencyFilter(-item.itemPrice) }}
                                </td>
                                <td class="no-wrap">
                                    {{ numberFilter(-item.returnedTotalCount) }}
                                </td>
                                <td class="no-wrap">
                                    {{ currencyFilter(-item.returnedTotalValue) }}
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th>{{ l('REPORTS.ITEM_SALES_TOTAL_HEADING') }}</th>
                                <th></th>
                                <th>{{ numberFilter(summary.itemSalesSummary.net.totalCount) }}</th>
                                <th>{{ currencyFilter(summary.itemSalesSummary.net.totalValue) }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { dateFilter } from '@/vue/filters/dateFilter';
import { mapState } from 'vuex';
import { numberFilter } from '@/vue/filters/numberFilter';
import { suppressZeroFilter } from '@/vue/filters/suppressZeroFilter';

export default {
    props: {
        extended: {
            type: Boolean,
            default: false,
        },

        printTitle: {
            type: String,
            default: '',
        },

        /**
         * @type {TradingSessionSummary}
         */
        summary: {
            type: Object,
            required: true,
        },

        /**
         * @type {TradingSession}
         */
        tradingSession: {
            type: Object,
            required: true,
        },
    },

    computed: {
        ...mapState(['isOnline']),

        /**
         * @returns {!Device}
         */
        currentDevice() {
            return container.currentDevice.device;
        },

        /**
         * @returns {string}
         */
        currentUsername() {
            return container.userService.getUsername();
        },

        /**
         * Add up the total of the cash up data.
         *
         * @return {number}
         */
        cashUpTotal() {
            const values = this.summary.tillContents.cashActualBreakdown.map((val) => {
                return val.value;
            });

            return values.reduce((acc, curr) => {
                return Number(acc + curr);
            }, 0);
        },
    },

    methods: {
        suppressZeroFilter,
        currencyFilter,
        numberFilter,
        dateFilter,
    },
};
</script>
